import { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { COUNTRIES } from '../../utils/countries';
import { ITEM_FILTER_SCHEMAS, getFilterOptions, matchFilter } from './division7FilterSchemas';

/**
 * Division 7 shared filter hook.
 *
 * @param {string}  itemType  – schema key, e.g. 'thermal-insulation'
 * @param {Array}   products  – raw product list from API
 * @returns {{ filtered, filters, setFilter, clearFilters, filterOptions, schema, activeCount }}
 */
export const useDivision7Filters = (itemType, products = []) => {
  const [filters, setFilters] = useState({});
  const [globalManufacturers, setGlobalManufacturers] = useState([]);

  const schema = useMemo(() => ITEM_FILTER_SCHEMAS[itemType] || [], [itemType]);

  // Fetch global manufacturers once (shared Div 7 collection)
  useEffect(() => {
    axios.get(`${API}/thermal-insulation-manufacturers`)
      .then(r => {
        const names = (r.data || []).map(m => m.name).filter(Boolean);
        setGlobalManufacturers([...new Set(names)].sort());
      })
      .catch(() => {});
  }, []);

  // All country codes for the Country filter, sorted by display name
  const allCountryCodes = useMemo(
    () => [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name)).map(c => c.code),
    []
  );

  // Dynamic options derived from current products + global sources
  const filterOptions = useMemo(() => {
    const opts = {};
    // Manufacturer: union of global list + product-derived (covers all cases)
    const productMfrs = products.map(p => p.manufacturerName).filter(Boolean);
    opts.manufacturerName = [...new Set([...globalManufacturers, ...productMfrs])].sort();
    // Country: full COUNTRIES list so dropdown is never empty
    opts.countryCode = allCountryCodes;
    // Item-specific
    schema.forEach(f => { opts[f.field] = getFilterOptions(f, products); });
    return opts;
  }, [products, schema, globalManufacturers, allCountryCodes]);

  const setFilter = useCallback((field, value) => {
    setFilters(prev => {
      if (!value) { const { [field]: _, ...rest } = prev; return rest; }
      return { ...prev, [field]: value };
    });
  }, []);

  const clearFilters = useCallback(() => setFilters({}), []);

  const filtered = useMemo(() => {
    const keys = Object.keys(filters);
    if (!keys.length) return products;
    return products.filter(p => keys.every(key => {
      const val = filters[key];
      if (!val) return true;
      // Common: text search
      if (key === 'search') return (p.productNameModel || '').toLowerCase().includes(val.toLowerCase());
      // Common: manufacturer
      if (key === 'manufacturerName') return p.manufacturerName === val;
      // Common: country
      if (key === 'countryCode') return p.countryCode === val;
      // Common: status
      if (key === 'status') return p.status === val;
      // Common: hasSubmittals
      if (key === 'hasSubmittals') return val === 'Yes' ? (p.submittals?.length > 0) : (p.submittals?.length === 0 || !p.submittals);
      // Item-specific
      const filterDef = schema.find(f => f.field === key);
      if (filterDef) return matchFilter(p, filterDef, val);
      return true;
    }));
  }, [products, filters, schema]);

  const activeCount = Object.keys(filters).length;

  return { filtered, filters, setFilter, clearFilters, filterOptions, schema, activeCount };
};
